import React, { useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

const leer = {
  label: '',
  auth_method: 'push_tan',
  bank_code: '',
  endpoint_url: '',
  bank_user: '',
  customer_id: '',
  account_iban: '',
  account_bic: '',
};

function meldung(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

function zugang(art) {
  if (art === 'push_tan') return 'Push-TAN';
  if (art === 'keyfile') return 'Schlüsseldatei';
  return 'TAN';
}

function teileAdresse(url) {
  const treffer = /^hbci:\/\/([^:/]+):(\d+)$/.exec(url || '');
  if (!treffer) return { rechner: '', port: '3000' };
  return { rechner: treffer[1], port: treffer[2] };
}

function liesDatei(file) {
  return new Promise((resolve, reject) => {
    const leser = new FileReader();
    leser.onload = () => {
      const text = String(leser.result || '');
      resolve(text.includes(',') ? text.split(',')[1] : '');
    };
    leser.onerror = () => reject(new Error('lesen'));
    leser.readAsDataURL(file);
  });
}

/**
 * Mehrere Banken je Mandant. Push-TAN, TAN zum Eintippen, oder die
 * RDH-10-Schlüsseldatei, mit der MoneyMoney dieselbe Bank erreicht.
 */
export default function FintsConnectionsDialog({ open, onOpenChange }) {
  const [liste, setListe] = useState([]);
  const [felder, setFelder] = useState(leer);
  const [bearbeiten, setBearbeiten] = useState(null);
  const [hatDatei, setHatDatei] = useState(false);
  const [schluessel, setSchluessel] = useState('');
  const [rechner, setRechner] = useState('');
  const [port, setPort] = useState('3000');
  const [hinweis, setHinweis] = useState('');
  const [busy, setBusy] = useState(false);

  const oeffne = (zeile) => {
    setBearbeiten(zeile.id);
    setHatDatei(!!zeile.has_keyfile);
    setSchluessel('');
    setHinweis('');
    setFelder({
      label: zeile.label || '',
      auth_method: zeile.auth_method || 'tan',
      bank_code: zeile.bank_code || '',
      endpoint_url: zeile.endpoint_url || '',
      bank_user: zeile.bank_user || '',
      customer_id: zeile.customer_id || '',
      account_iban: zeile.account_iban || '',
      account_bic: zeile.account_bic || '',
    });
    const adresse = teileAdresse(zeile.endpoint_url);
    setRechner(adresse.rechner);
    setPort(adresse.port);
  };

  const neueVerbindung = () => {
    setFelder(leer);
    setBearbeiten(null);
    setHatDatei(false);
    setSchluessel('');
    setRechner('');
    setPort('3000');
    setHinweis('');
  };

  const laden = (ersteOeffnen = false) => {
    api.get('/sepa/fints/connections')
      .then((r) => {
        const zeilen = Array.isArray(r.data) ? r.data : [];
        setListe(zeilen);
        if (ersteOeffnen && zeilen.length > 0) oeffne(zeilen[0]);
      })
      .catch(() => setListe([]));
  };

  useEffect(() => {
    if (!open) return;
    neueVerbindung();
    laden(true);
  }, [open]);

  const waehleDatei = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    try {
      const b64 = await liesDatei(file);
      setSchluessel(b64);
      const r = await api.post('/sepa/fints/keyfile', { keyfile_b64: b64 });
      const daten = r.data || {};
      const adresse = teileAdresse(daten.endpoint_url);
      setRechner(adresse.rechner);
      setPort(adresse.port);
      setHinweis(daten.hinweis || '');
      setFelder((alt) => ({
        ...alt,
        bank_code: daten.bank_code || alt.bank_code,
        bank_user: daten.bank_user || alt.bank_user,
        customer_id: daten.customer_id || alt.customer_id,
        endpoint_url: daten.endpoint_url || alt.endpoint_url,
      }));
    } catch (error) {
      toast.error(meldung(error, 'Die Schlüsseldatei lässt sich nicht lesen.'));
    }
  };

  const speichern = async () => {
    setBusy(true);
    try {
      const koerper = { ...felder };
      if (felder.auth_method === 'keyfile') {
        koerper.endpoint_url = `hbci://${rechner}:${port}`;
        if (schluessel) koerper.keyfile_b64 = schluessel;
      }
      if (bearbeiten) {
        await api.put(`/sepa/fints/connections/${bearbeiten}`, koerper);
      } else {
        await api.post('/sepa/fints/connections', koerper);
      }
      toast.success('Bankverbindung gespeichert.');
      setFelder(leer);
      setBearbeiten(null);
      setHatDatei(false);
      setSchluessel('');
      setRechner('');
      setPort('3000');
      setHinweis('');
      laden();
    } catch (error) {
      toast.error(meldung(error, 'Die Bankverbindung konnte nicht gespeichert werden.'));
    } finally {
      setBusy(false);
    }
  };

  const entfernen = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/sepa/fints/connections/${id}`);
      if (bearbeiten === id) {
        setBearbeiten(null);
        setFelder(leer);
        setHatDatei(false);
      }
      laden();
    } catch (error) {
      toast.error(meldung(error, 'Die Bankverbindung konnte nicht gelöscht werden.'));
    } finally {
      setBusy(false);
    }
  };

  const dateiFehlt = felder.auth_method === 'keyfile' && !schluessel && !(bearbeiten && hatDatei);
  const kontoFehlt = !felder.account_iban.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="fints-connections-dialog">
        <DialogHeader>
          <DialogTitle>Bankverbindungen</DialogTitle>
          <DialogDescription>
            Je Bank eine Verbindung. PIN und das Passwort der Schlüsseldatei werden erst beim Senden abgefragt.
          </DialogDescription>
        </DialogHeader>

        {liste.length > 0 && (
          <div className="space-y-2">
            <ul className="space-y-2">
              {liste.map((z) => (
                <li key={z.id} className="flex items-center gap-2 text-sm border rounded-md px-3 py-2">
                  <button type="button" className="flex-1 text-left" onClick={() => oeffne(z)} data-testid={`fints-connection-${z.id}`}>
                    <span className="font-medium">{z.label || z.bank_code}</span>
                    <span className="text-slate-500">
                      {' '}· {zugang(z.auth_method)} · {z.account_iban
                        ? `Konto •••• ${z.account_iban.replace(/\s/g, '').slice(-4)}`
                        : 'Konto noch zuordnen'}
                    </span>
                  </button>
                  <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => entfernen(z.id)} aria-label="Bankverbindung löschen">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
            <Button type="button" variant="outline" size="sm" onClick={neueVerbindung}>
              Weitere Bank hinzufügen
            </Button>
          </div>
        )}

        {bearbeiten && (
          <p
            className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3"
            data-testid="bank-edit-hinweis"
          >
            Sie bearbeiten „{felder.label || felder.bank_code}“. Für eine andere Bank bitte
            {' '}„Weitere Bank hinzufügen“ verwenden.
          </p>
        )}

        <div className="space-y-3">
          <div>
            <Label htmlFor="bank-label">Bezeichnung</Label>
            <Input id="bank-label" value={felder.label} data-testid="bank-label"
              onChange={(e) => setFelder({ ...felder, label: e.target.value })}
              placeholder="Grafschafter Volksbank" />
          </div>
          <div>
            <Label htmlFor="bank-method">Zugang</Label>
            <select
              id="bank-method"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={felder.auth_method}
              data-testid="bank-method"
              disabled={!!bearbeiten}
              onChange={(e) => setFelder({ ...felder, auth_method: e.target.value })}
            >
              <option value="push_tan">Push-TAN</option>
              <option value="tan">TAN eingeben</option>
              <option value="keyfile">Schlüsseldatei</option>
            </select>
          </div>
          {felder.auth_method === 'keyfile' && (
            <>
              <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="bank-keyfile-hinweis">
                Dieselbe RDH-10-Schlüsseldatei wie in MoneyMoney. Sie wird verschlüsselt gespeichert,
                das Passwort nur beim Senden. MoneyMoney sollte diese Datei nicht gleichzeitig benutzen,
                sonst läuft der Signaturzähler auseinander.
              </p>
              <div>
                <Label htmlFor="bank-keyfile">Schlüsseldatei</Label>
                <input
                  id="bank-keyfile"
                  type="file"
                  accept=".rdh10,.rdh,.key"
                  className="block w-full text-sm"
                  data-testid="bank-keyfile"
                  onChange={waehleDatei}
                />
                {bearbeiten && hatDatei && !schluessel && (
                  <p className="text-xs text-slate-500 mt-1">Eine Datei ist schon hinterlegt. Nur zum Ersetzen neu wählen.</p>
                )}
              </div>
              {hinweis && (
                <p className="text-sm text-slate-600" data-testid="bank-keyfile-host">{hinweis}</p>
              )}
              <div>
                <Label htmlFor="bank-host">Rechner</Label>
                <Input id="bank-host" value={rechner} autoComplete="off" data-testid="bank-host"
                  onChange={(e) => setRechner(e.target.value)}
                  placeholder="fints1.atruvia.de" />
              </div>
              <div>
                <Label htmlFor="bank-port">Port</Label>
                <Input id="bank-port" value={port} inputMode="numeric" autoComplete="off" data-testid="bank-port"
                  onChange={(e) => setPort(e.target.value)} />
              </div>
            </>
          )}
          {felder.auth_method !== 'keyfile' && (
            <div>
              <Label htmlFor="bank-url">FinTS-Adresse</Label>
              <Input id="bank-url" value={felder.endpoint_url} placeholder="https://" autoComplete="off" data-testid="bank-url"
                onChange={(e) => setFelder({ ...felder, endpoint_url: e.target.value })} />
            </div>
          )}
          <div>
            <Label htmlFor="bank-blz">Bankleitzahl</Label>
            <Input id="bank-blz" value={felder.bank_code} inputMode="numeric" autoComplete="off" data-testid="bank-blz"
              disabled={!!bearbeiten}
              onChange={(e) => setFelder({ ...felder, bank_code: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="bank-user">Benutzerkennung</Label>
            <Input id="bank-user" value={felder.bank_user} autoComplete="off" data-testid="bank-user"
              onChange={(e) => setFelder({ ...felder, bank_user: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="bank-customer">Kunden-ID, falls die Bank sie getrennt führt</Label>
            <Input id="bank-customer" value={felder.customer_id} autoComplete="off" data-testid="bank-customer"
              onChange={(e) => setFelder({ ...felder, customer_id: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="bank-account-iban">Zugehöriges Konto (IBAN)</Label>
            <Input id="bank-account-iban" value={felder.account_iban} autoComplete="off"
              data-testid="bank-account-iban"
              onChange={(e) => setFelder({ ...felder, account_iban: e.target.value })}
              placeholder="DE…" />
            <p className="text-xs text-slate-500 mt-1">
              Kontostand und Umsätze werden ausschließlich für dieses Konto abgerufen.
            </p>
          </div>
          <div>
            <Label htmlFor="bank-account-bic">BIC des Kontos (optional)</Label>
            <Input id="bank-account-bic" value={felder.account_bic} autoComplete="off"
              data-testid="bank-account-bic"
              onChange={(e) => setFelder({ ...felder, account_bic: e.target.value })} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
          <Button onClick={speichern} disabled={busy || dateiFehlt || kontoFehlt} data-testid="bank-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {bearbeiten ? 'Änderung speichern' : 'Bank hinzufügen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
